import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../lib/api';
import { Id } from '../convex/_generated/dataModel';
import PhoneInput from './PhoneInput';
import { ErrorDisplay } from './ErrorDisplay';
import { formatError, logError } from '../utils/errorHandler';

export default function ManageTournament() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();

  const tournament = useQuery(
    api.tournaments.getTournamentDetails,
    tournamentId ? { tournamentId: tournamentId as Id<"tournaments"> } : "skip"
  );

  const updateTournament = useMutation(api.tournaments.updateTournament);
  const cancelTournament = useMutation(api.tournaments.cancelTournament);
  const lockTournament = useMutation(api.tournaments.lockTournament);
  const addPlayerToTournament = useMutation(api.tournaments.addPlayerToTournament);
  const removeFromTournament = useMutation(api.players.removeFromTournament);
  const setTournamentWinners = useMutation(api.tournaments.setTournamentWinners);

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    club: '',
    format: 'americano' as 'americano' | 'king',
    maxPlayers: 16,
    description: '',
    logo: '',
    registrationType: 'individual' as 'individual' | 'couples',
  });

  const [addPlayerModal, setAddPlayerModal] = useState(false);
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    phone: '',
    countryCode: '+52',
    partnerName: '',
    partnerPhone: '',
    partnerCountryCode: '+52',
  });

  const [winnersModal, setWinnersModal] = useState(false);
  const [selectedWinners, setSelectedWinners] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (tournament) {
      setFormData({
        name: tournament.name,
        date: new Date(tournament.date).toISOString().split('T')[0],
        time: tournament.time || '18:00',
        club: tournament.club,
        format: tournament.format,
        maxPlayers: tournament.maxPlayers,
        description: tournament.description || '',
        logo: tournament.logo || '',
        registrationType: tournament.registrationType || 'individual',
      });
    }
  }, [tournament]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateTournament({
        tournamentId: tournamentId as Id<"tournaments">,
        name: formData.name,
        date: new Date(formData.date).getTime(),
        time: formData.time,
        club: formData.club,
        format: formData.format,
        maxPlayers: formData.maxPlayers,
        description: formData.description,
        logo: formData.logo,
        registrationType: formData.registrationType,
      });

      setSuccess('Torneo actualizado exitosamente');
      setEditMode(false);
    } catch (err: any) {
      logError('ManageTournament:update', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('¿Estás seguro de que quieres cancelar este torneo? Se notificará a todos los jugadores.')) {
      setLoading(true);
      try {
        await cancelTournament({ tournamentId: tournamentId as Id<"tournaments"> });
        navigate('/dashboard/organizer');
      } catch (err: any) {
        setError(err.message || 'Error al cancelar el torneo');
        setLoading(false);
      }
    }
  };

  const handleLock = async () => {
    if (window.confirm('¿Estás seguro de que quieres bloquear el registro? No se permitirán más inscripciones.')) {
      setLoading(true);
      try {
        await lockTournament({ tournamentId: tournamentId as Id<"tournaments"> });
        setSuccess('Torneo bloqueado exitosamente');
        // Don't reload the page - the useQuery hook will automatically update
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        logError('ManageTournament:lock', err);
        setError(formatError(err));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const fullPhone = newPlayer.countryCode + newPlayer.phone;
      const partnerData = tournament?.registrationType === 'couples' && newPlayer.partnerName
        ? {
            partnerName: newPlayer.partnerName,
            partnerPhone: newPlayer.partnerCountryCode + newPlayer.partnerPhone,
          }
        : {};

      await addPlayerToTournament({
        tournamentId: tournamentId as Id<"tournaments">,
        playerName: newPlayer.name,
        playerPhone: fullPhone,
        ...partnerData,
      });

      setSuccess('Jugador agregado exitosamente');
      setAddPlayerModal(false);
      setNewPlayer({
        name: '',
        phone: '',
        countryCode: '+52',
        partnerName: '',
        partnerPhone: '',
        partnerCountryCode: '+52',
      });
      // Don't reload the page - the useQuery hook will automatically update
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      logError('ManageTournament:addPlayer', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayer = async (playerId: Id<"players">) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar a este jugador?')) {
      try {
        await removeFromTournament({
          tournamentId: tournamentId as Id<"tournaments">,
          playerId,
        });
        setSuccess('Jugador eliminado exitosamente');
        // Don't reload the page - the useQuery hook will automatically update
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Error al eliminar jugador');
      }
    }
  };

  const handleSetWinners = async () => {
    if (selectedWinners.length === 0) {
      setError('Por favor selecciona al menos un ganador');
      return;
    }

    setLoading(true);
    try {
      await setTournamentWinners({
        tournamentId: tournamentId as Id<"tournaments">,
        winnerIds: selectedWinners as Id<"players">[],
      });

      setSuccess('Ganadores registrados exitosamente');
      setWinnersModal(false);
      setSelectedWinners([]);
      // Don't reload the page - the useQuery hook will automatically update
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al registrar ganadores');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/tournament/${tournament?.inviteCode}`;
    const text = `¡Únete a nuestro torneo de pádel!\n${tournament?.name}\n${tournament?.club}\n${new Date(tournament?.date || 0).toLocaleDateString('es-MX')}`;

    if (navigator.share) {
      navigator.share({
        title: tournament?.name,
        text,
        url,
      });
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      setSuccess('Enlace copiado al portapapeles');
    }
  };

  if (!tournament) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const confirmedPlayers = tournament.registrations.filter((r: any) => r.status === 'confirmed');
  const waitlistPlayers = tournament.registrations.filter((r: any) => r.status === 'waitlist');

  // Calculate actual player count - for couples, each registration counts as 2 players
  const actualPlayerCount = tournament.registrationType === 'couples'
    ? confirmedPlayers.length * 2
    : confirmedPlayers.length;

  const actualWaitlistCount = tournament.registrationType === 'couples'
    ? waitlistPlayers.length * 2
    : waitlistPlayers.length;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <img
                src="/torneopadel.png"
                alt="TorneoPadel"
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-3xl font-bold text-gray-900">Gestionar Torneo</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard/organizer')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Volver al Dashboard
            </button>
          </div>

          <ErrorDisplay error={error} onDismiss={() => setError('')} />

          {success && (
            <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg">
              {success}
            </div>
          )}

          {/* Tournament Details */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Detalles del Torneo</h2>
              <div className="flex gap-2">
                {!editMode && tournament.status === 'open' && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Editar
                  </button>
                )}
                {tournament.status === 'open' && (
                  <button
                    onClick={handleLock}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                  >
                    Bloquear Registro
                  </button>
                )}
                <button
                  onClick={handleShare}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  Compartir
                </button>
                {tournament.status !== 'cancelled' && tournament.status !== 'completed' && (
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    Cancelar Torneo
                  </button>
                )}
              </div>
            </div>

            {editMode ? (
              <form onSubmit={handleUpdate} className="space-y-4">
                {/* Row 1: Nombre del Torneo (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Torneo
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                {/* Row 2: Fecha and Hora */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora
                    </label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                {/* Row 3: Club/Ubicación (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Club/Ubicación
                  </label>
                  <input
                    type="text"
                    value={formData.club}
                    onChange={(e) => setFormData({ ...formData, club: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                {/* Row 4: Formato and Tipo de Inscripción */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Formato
                    </label>
                    <select
                      value={formData.format}
                      onChange={(e) => setFormData({ ...formData, format: e.target.value as 'americano' | 'king' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="americano">Americano</option>
                      <option value="king">Rey de la Cancha</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Inscripción
                    </label>
                    <select
                      value={formData.registrationType}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    >
                      <option value="individual">Individual</option>
                      <option value="couples">Por Parejas</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Este campo no se puede modificar después de crear el torneo
                    </p>
                  </div>
                </div>

                {/* Row 5: Jugadores Máximos (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jugadores Máximos
                  </label>
                  <input
                    type="number"
                    value={formData.maxPlayers}
                    onChange={(e) => setFormData({ ...formData, maxPlayers: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    min="4"
                    max="32"
                    required
                  />
                </div>

                {/* Row 6: Instrucciones (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instrucciones para los Jugadores
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="ej. Costo: $200 por persona. Pagar en la cancha. Llegar 15 minutos antes. Incluye agua y pelotas. Premios para los ganadores."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Información importante como: costo, forma de pago, hora de llegada, premios, etc.
                  </p>
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-semibold">Nombre:</span> {tournament.name}
                  </div>
                  <div>
                    <span className="font-semibold">Club:</span> {tournament.club}
                  </div>
                  <div>
                    <span className="font-semibold">Fecha:</span> {new Date(tournament.date).toLocaleDateString('es-MX')}
                  </div>
                  <div>
                    <span className="font-semibold">Hora:</span> {tournament.time}
                  </div>
                  <div>
                    <span className="font-semibold">Formato:</span> {tournament.format === 'americano' ? 'Americano' : 'Rey de la Cancha'}
                  </div>
                  <div>
                    <span className="font-semibold">Tipo de Inscripción:</span> {tournament.registrationType === 'couples' ? 'Por Parejas' : 'Individual'}
                  </div>
                  <div>
                    <span className="font-semibold">Estado:</span>{' '}
                    <span className={`px-2 py-1 rounded text-sm ${
                      tournament.status === 'open' ? 'bg-green-100 text-green-800' :
                      tournament.status === 'locked' ? 'bg-yellow-100 text-yellow-800' :
                      tournament.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tournament.status === 'open' ? 'Abierto' :
                       tournament.status === 'locked' ? 'Bloqueado' :
                       tournament.status === 'completed' ? 'Completado' : 'Cancelado'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Código de Invitación:</span>{' '}
                    <span className="font-mono bg-gray-200 px-2 py-1 rounded">{tournament.inviteCode}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Jugadores:</span> {actualPlayerCount}/{tournament.maxPlayers}
                  </div>
                </div>
                {tournament.description && (
                  <div className="mt-4">
                    <span className="font-semibold">Descripción:</span>
                    <p className="mt-1 text-gray-600">{tournament.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Players Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                Jugadores Confirmados ({actualPlayerCount}/{tournament.maxPlayers})
              </h2>
              {tournament.status === 'open' && (
                <button
                  onClick={() => setAddPlayerModal(true)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {tournament.registrationType === 'couples' ? 'Agregar Pareja' : 'Agregar Jugador'}
                </button>
              )}
            </div>

            {confirmedPlayers.length > 0 ? (
              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {tournament.registrationType === 'couples' ? (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pareja
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Teléfonos
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nombre
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Teléfono
                          </th>
                        </>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tournament.registrationType === 'couples' ? (
                      // For couples, group and display both players in one row
                      confirmedPlayers
                        .filter((reg: any, _index: number, array: any[]) => {
                          // Show only one entry per couple (avoid duplicates)
                          // Check if this is the first player of the couple
                          const partnerReg = array.find((r: any) =>
                            r.playerId === reg.partnerId || r.partnerId === reg.playerId
                          );
                          if (!partnerReg) return true; // Solo player
                          // Show only the first occurrence of the couple
                          return array.indexOf(reg) <= array.indexOf(partnerReg);
                        })
                        .map((reg: any) => {
                          const partnerReg = confirmedPlayers.find((r: any) =>
                            r.playerId === reg.partnerId
                          );
                          return (
                            <tr key={reg._id}>
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {reg.player?.name || 'Unknown'}
                                  </span>
                                  <span className="text-gray-600">
                                    {partnerReg?.player?.name || reg.partnerName || 'Sin pareja'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col text-sm">
                                  <span>{reg.player?.phoneNumber || 'N/A'}</span>
                                  <span className="text-gray-600">
                                    {partnerReg?.player?.phoneNumber || reg.partnerPhone || 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleRemovePlayer(reg.playerId)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Eliminar Pareja
                                </button>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      // For individual registrations
                      confirmedPlayers.map((reg: any) => (
                        <tr key={reg._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {reg.player?.name || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {reg.player?.phoneNumber || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleRemovePlayer(reg.playerId)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
                No hay jugadores registrados aún
              </div>
            )}
          </div>

          {/* Waitlist Section */}
          {waitlistPlayers.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">
                Lista de Espera ({actualWaitlistCount})
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <ul className="space-y-2">
                  {waitlistPlayers.map((reg: any) => (
                    <li key={reg._id} className="flex justify-between items-center">
                      <span>{reg.player?.name || 'Unknown'}</span>
                      <span className="text-sm text-gray-500">{reg.player?.phoneNumber}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Winners Section */}
          {tournament.status === 'completed' && tournament.winners && tournament.winners.length > 0 ? (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">🏆 Ganadores</h2>
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <ul className="space-y-2">
                  {confirmedPlayers
                    .filter((reg: any) => tournament.winners?.includes(reg.playerId))
                    .map((reg: any) => (
                      <li key={reg._id} className="font-semibold">
                        {reg.player?.name}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          ) : tournament.status !== 'cancelled' && tournament.status !== 'open' ? (
            <div className="mb-8">
              <button
                onClick={() => setWinnersModal(true)}
                className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              >
                Registrar Ganadores
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Add Player Modal */}
      {addPlayerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">
              {tournament.registrationType === 'couples' ? 'Agregar Pareja de Jugadores' : 'Agregar Jugador'}
            </h3>
            <form onSubmit={handleAddPlayer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tournament.registrationType === 'couples' ? 'Nombre del Jugador 1' : 'Nombre del Jugador'}
                </label>
                <input
                  type="text"
                  value={newPlayer.name}
                  onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tournament.registrationType === 'couples' ? 'Teléfono del Jugador 1' : 'Teléfono del Jugador'}
                </label>
                <PhoneInput
                  countryCode={newPlayer.countryCode}
                  phoneNumber={newPlayer.phone}
                  onCountryCodeChange={(code) => setNewPlayer({ ...newPlayer, countryCode: code })}
                  onPhoneNumberChange={(number) => setNewPlayer({ ...newPlayer, phone: number })}
                  required
                />
              </div>

              {tournament.registrationType === 'couples' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Jugador 2
                    </label>
                    <input
                      type="text"
                      value={newPlayer.partnerName}
                      onChange={(e) => setNewPlayer({ ...newPlayer, partnerName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono del Jugador 2
                    </label>
                    <PhoneInput
                      countryCode={newPlayer.partnerCountryCode}
                      phoneNumber={newPlayer.partnerPhone}
                      onCountryCodeChange={(code) => setNewPlayer({ ...newPlayer, partnerCountryCode: code })}
                      onPhoneNumberChange={(number) => setNewPlayer({ ...newPlayer, partnerPhone: number })}
                      required
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setAddPlayerModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {winnersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Seleccionar Ganadores</h3>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {confirmedPlayers.map((reg: any) => (
                <label key={reg._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    value={reg.playerId}
                    checked={selectedWinners.includes(reg.playerId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWinners([...selectedWinners, reg.playerId]);
                      } else {
                        setSelectedWinners(selectedWinners.filter(id => id !== reg.playerId));
                      }
                    }}
                    className="w-4 h-4 text-green-600"
                  />
                  <span>{reg.player?.name}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={() => setWinnersModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSetWinners}
                disabled={loading || selectedWinners.length === 0}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                {loading ? 'Registrando...' : 'Registrar Ganadores'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}